import { downloadBlob } from '@shared/utils'
import type { PropertyAgreement } from '../types/listingRequestTypes'

export interface AgreementDocumentAction {
  path: string
  icon: string
  label: string
}

// The signed copy once there is one; until then, the unsigned draft to read.
// `icon` / `label` describe the single-button form used by the version history.
export function documentOf(agreement: PropertyAgreement): AgreementDocumentAction | null {
  if (agreement.signedFilePath) {
    return { path: agreement.signedFilePath, icon: 'download', label: 'Download signed document' }
  }
  if (agreement.unsignedFilePath) {
    return { path: agreement.unsignedFilePath, icon: 'eye', label: 'View document' }
  }
  return null
}

export function openDocument(path: string | null): void {
  if (path) window.open(path, '_blank', 'noopener')
}

// The file's own name from its storage URL, for the saved copy.
function fileNameOf(path: string, fallback: string): string {
  const last = path.split(/[?#]/)[0]?.split('/').pop()
  return last ? decodeURIComponent(last) : fallback
}

/**
 * Saves a stored document to the seller's device. Fetched as a blob so the
 * browser saves it instead of navigating; when the storage host refuses the
 * cross-origin read, it opens in a new tab instead, where the browser's own
 * save still works — the seller always gets the file one way or the other.
 */
export async function downloadDocument(path: string | null, fallbackName: string): Promise<void> {
  if (!path) return
  try {
    const response = await fetch(path)
    if (response.ok) {
      downloadBlob(await response.blob(), fileNameOf(path, fallbackName))
      return
    }
  } catch {
    // CORS refusal or network error — fall through to the new-tab path.
  }
  openDocument(path)
}
